import { forget, remember } from '@epic-web/remember';
import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';

import { APIDomain } from 'types';
import { SpoofingClientId } from 'types/spoofing';
import type { UiBootstrapResponse } from 'types/ui-bootstrap';
import { ONE_HOUR, SPOOFED_LS_KEY } from 'utils/constants';
import { getError } from 'utils/errors';

const AUTH_SESSION_KEY = 'auth-session-request';

export const dedupedFetchAuthSession = () => {
  return remember(AUTH_SESSION_KEY, () => {
    return fetchAuthSession().finally(() => {
      clearAuthSessionCache();
    });
  });
};

export function clearAuthSessionCache() {
  forget(AUTH_SESSION_KEY);
}

function checkStatus(response: Response) {
  const isValidStatusCode = response.status >= 200 && response.status < 300;
  if (!isValidStatusCode) {
    throw response;
  }
}

function extractData<JSONResponse>(json: { data: JSONResponse }) {
  const { data } = json;
  if (!data) {
    throw new Error('Invalid JSON Response');
  }
  return data;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      onError(err) {
        const pathname = window.location.pathname;
        if (err === 'No current user' && !pathname.startsWith('/external')) {
          window.location.href = `${window.location.origin}/login`;
        } else {
          const { message, status, statusText } = getError(err);
          Sentry.captureException(message, {
            extra: {
              message,
              status,
              statusText,
            },
          });
        }
      },
    },
  },
});
export enum QUERIES {
  // UiBootstrap
  UiBootstrap = 'UiBootstrap',
  // dashboard
  dashboardKPIs = 'dashboard-KPIs',
  dashboardBreakdownByAge = 'dashboard-BreakdownByAge',
  dashboardInflowOutflow = 'dashboard-InflowOutflow',
  dashboardExcTeams = 'dashboard-ExcTeams',
  dashboardExcUsers = 'dashboard-ExcUsers',
  dashboardExcVendors = 'dashboard-ExcVendors',
  dashboardExcRootCause = 'dashboard-ExcRootCause',
  // filter search queries
  itemQuickSearch = 'item-quick-search',
  mfgNameSearch = 'mfg-name-search',
  requestingLocationQuickSearch = 'requesting-location-quick-search',
  vendorNameSearch = 'vendor-name-search',
  departmentSearch = 'department-search',
}

type SearchParams = string | string[][] | Record<string, string> | URLSearchParams | undefined;

export interface FetchOptions<PostData> {
  params?: SearchParams;
  data?: PostData;
  file?: File;
  headers?: RequestInit['headers'];
  method?: RequestInit['method'];
  ignoreSpoof?: boolean;
  signal?: AbortSignal;
}

export function handleSpoofing(): RequestInit['headers'] {
  try {
    const saved = localStorage.getItem(SPOOFED_LS_KEY);
    if (saved !== null) {
      const spoofed: SpoofingClientId = JSON.parse(saved);

      if (spoofed !== null) {
        if (spoofed.startTime && spoofed.startTime + ONE_HOUR > Date.now()) {
          console.log(`[api] spoofed client id: ${spoofed.spoofedClient}`);
          console.log(`[api] spoofed user id: ${spoofed.spoofedUser}`);
          return {
            'X-CLIENT-ID-TO-SPOOF': spoofed.spoofedClient,
            'X-USER-ID-TO-SPOOF': spoofed.spoofedUser,
            'X-CLIENT-ID': spoofed.myClient,
          };
        } else {
          console.log(`[api] spoofed client id expired`);
          localStorage.removeItem(SPOOFED_LS_KEY);
          alert('Spoofing time has expired, please spoof again');
          window.location.href === '/spoof';
        }
      }
    }
  } catch {
    return {};
  }
  return {};
}

export enum APIDomains {
  api = 'api',
  jit = 'api-jit',
  rest = 'rest',
}

export function getJitUrl(endpoint: string) {
  if (process.env.VITE_DEPLOY_ENV !== 'development') {
    return `${process.env.VITE_JIT_API}${endpoint}`;
  }
  return `/api-jit${endpoint}`;
}

export function getFullRestUrl(endpoint: string) {
  return `${process.env.VITE_REST_API}${endpoint}`;
}

export function getUrl(endpoint: string, api: APIDomain) {
  if (process.env.VITE_DEPLOY_ENV !== 'development') {
    switch (api) {
      case 'jit':
        return `${process.env.VITE_JIT_API}${endpoint}`;
      case 'rest':
        return `${process.env.VITE_REST_API}${endpoint}`;
      case 'api':
        return `${process.env.VITE_API_URL}${endpoint}`;
      default:
        return `${process.env.VITE_API_URL}${endpoint}`;
    }
  }
  return `/${APIDomains[api]}${endpoint}`;
}

type FetchApiFunctionArgs<PostData> = {
  endpoint: string;
  options?: FetchOptions<PostData>;
  api?: APIDomain;
  isPublic?: boolean;
};

/**
 * Fetch data from the Clarium API
 * Automatically includes authentication in headers if available
 *
 * @param options Any additional XHR options
 */
export async function fetchApi<JSONResponse, PostData>({
  endpoint,
  options = {},
  api = 'api',
  isPublic,
}: FetchApiFunctionArgs<PostData>): Promise<JSONResponse> {
  try {
    // Check endpoint and build url
    if (!endpoint.startsWith('/')) {
      throw new Error('Invalid API Endpoint');
    }

    // Build URL
    let url = getUrl(endpoint, api);

    // Build options
    const { params, data, file, ...rest } = options;
    const search = new URLSearchParams(params).toString();
    if (search) {
      url += `?${search}`;
    }

    const opt: RequestInit = {
      method: 'GET',
      headers: {},
      credentials: 'same-origin',
      ...rest,
    };

    // Build JSON body from data object
    if (data) {
      opt.body = JSON.stringify(data);
      options.headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };
    } else if (file) {
      opt.body = file;
      options.headers = {
        'Content-Type': file.type,
        ...options.headers,
      };
    }

    if (!isPublic) {
      try {
        const session = await dedupedFetchAuthSession();
        const accessToken = session.tokens?.idToken;
        opt.headers = {
          Authorization: `Bearer ${accessToken}`,
          ...(options.ignoreSpoof ? {} : handleSpoofing()),
          ...options.headers,
        };
      } catch (error) {
        // This is an error from Cognito. – 'No current user'
        // It is handled in the onError function of the queryClient
        const err = getError(error);
        if (err.message !== 'Unauthenticated access is not supported for this identity pool.') {
          console.log(endpoint);
        } else {
          console.log('UNAUTHORIZED.', endpoint);
        }
        console.error(error);

        throw error;
      }
    } else {
      opt.headers = {
        ...options.headers,
      };
    }

    // Send request
    // debug.log('[fetchApi] URL/Options', url, opt);
    const response = await fetch(url, opt);

    // Check HTTP status code
    await checkStatus(response);

    const contentType = response.headers.get('content-type');

    if (options.method === 'DELETE' || response.status === 204) {
      return {} as unknown as JSONResponse;
    }

    if (contentType && !contentType.includes('application/json')) {
      try {
        const blob = await response.blob();
        return blob as unknown as JSONResponse;
      } catch (error) {
        console.error(error);
      }
    }
    // Parse response
    const json: { data: JSONResponse } = await response.json();
    // Extract data
    return extractData<JSONResponse>(json);
  } catch (error: unknown) {
    const err = getError(error);
    if (err.message !== 'Unauthenticated access is not supported for this identity pool.') {
      console.log(error, endpoint);
    } else {
      console.log('UNAUTHORIZED.', endpoint);
    }

    throw error;
  }
}

//
// Endpoints
//

const fetchUiBootstrap = (): Promise<UiBootstrapResponse> => {
  const endpoint = '/v1/ui_bootstrap';
  return fetchApi({ endpoint, options: {}, api: 'rest' });
};

export function bootstrapQuery() {
  return {
    queryKey: [QUERIES.UiBootstrap],
    queryFn: () => fetchUiBootstrap(),
    staleTime: ONE_HOUR,
    retry: false,
  };
}

export function convertFiltersToSearchParams(
  params: Record<string, string | string[] | number>
): Record<string, string> {
  const nextParams: Record<string, string> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      if (Array.isArray(value)) {
        nextParams[key] = value.join(',');
      } else {
        nextParams[key] = value.toString();
      }
    }
  });
  return nextParams;
}
